import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { loginClient } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from || '/';
  const verified = searchParams.get('verified') === 'true';
  const tokenError = searchParams.get('error') === 'lien-invalide';

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await loginClient(form);
      login(res.data.token, res.data.client);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Email ou mot de passe incorrect.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
            {/* Panneau gauche */}
            <div className="hidden md:flex md:w-2/5 bg-black flex-col justify-center p-12">
              <p className="text-3xl font-black tracking-widest text-white uppercase mb-2">
                ROYAL <span className="text-yellow-400">CARS</span>
              </p>
              <p className="text-gray-400 text-sm uppercase tracking-widest mb-10">Votre espace personnel</p>
              <ul className="space-y-5">
                {[
                  { icon: '♥', text: 'Retrouvez vos véhicules favoris' },
                  { icon: '🔔', text: 'Suivez les nouveaux arrivages' },
                  { icon: '🔒', text: 'Espace sécurisé et privé' },
                ].map(({ icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <span className="text-yellow-400 text-xl">{icon}</span>
                    <span className="text-gray-300 text-sm leading-relaxed">{text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 pt-8 border-t border-gray-800">
                <p className="text-gray-500 text-sm">Pas encore de compte ?</p>
                <Link to="/inscription" className="text-yellow-400 font-bold hover:text-yellow-300 transition text-sm">
                  Créer un compte →
                </Link>
              </div>
            </div>

            {/* Formulaire */}
            <div className="flex-1 p-8 md:p-12 flex flex-col justify-center">
              <div className="md:hidden mb-6">
                <p className="text-2xl font-black tracking-widest text-black uppercase">
                  ROYAL <span className="text-yellow-400">CARS</span>
                </p>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Se connecter</h1>
              <p className="text-gray-500 text-sm mb-8">Accédez à votre espace personnel.</p>

              {verified && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <div>
                    <p className="text-green-800 font-semibold text-sm">Email confirmé !</p>
                    <p className="text-green-700 text-sm">Votre adresse email a été vérifiée. Vous pouvez maintenant vous connecter.</p>
                  </div>
                </div>
              )}

              {tokenError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-700 text-sm">Le lien de confirmation est invalide ou a expiré.</p>
                </div>
              )}

              {location.state?.message && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-700 text-sm">{location.state.message}</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <span className="text-red-500 text-lg">⚠</span>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse email</label>
                  <input
                    type="email" name="email" value={form.email} onChange={handleChange} required
                    placeholder="jean.dupont@email.com"
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm focus:border-black focus:outline-none transition"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Mot de passe</label>
                    <Link to="/mot-de-passe-oublie" className="text-xs text-yellow-500 hover:text-yellow-400 font-semibold transition">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange} required
                      placeholder="••••••••"
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 pr-12 text-sm focus:border-black focus:outline-none transition"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>

                <button
                  type="submit" disabled={loading}
                  className="w-full bg-black text-white py-4 rounded-lg font-bold text-sm hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </button>

                <p className="text-center text-sm text-gray-500">
                  Pas encore de compte ?{' '}
                  <Link to="/inscription" className="text-yellow-500 font-bold hover:text-yellow-400 transition">
                    Créer un compte
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
